// Card lookups and small helpers shared by the machines.
import type { Card, Deck, DeckContext, LearnUse, Pack } from '../../../shared/pack/schema'
import type { Rng } from '../rng'

export interface Indexed {
  card: Card
  deck: Deck
}

const cache = new WeakMap<Pack, Map<string, Indexed>>()

export function cardIndex(pack: Pack): Map<string, Indexed> {
  let m = cache.get(pack)
  if (!m) {
    m = new Map()
    for (const deck of pack.decks) for (const card of deck.cards) m.set(card.id, { card, deck })
    cache.set(pack, m)
  }
  return m
}

export function lookup(pack: Pack, id: string): Indexed | undefined {
  return cardIndex(pack).get(id)
}

/** The deck's context for this card (its first tag the deck names), as the deck gives it. */
function deckContext(card: Card, deck: Deck): DeckContext | undefined {
  for (const t of card.tags) {
    const c = deck.contexts?.[t]
    if (c) return c
  }
  return undefined
}

/** Where you'd meet this card ("At the till"): the card's own, else its deck's first matching tag. */
export function contextFor(card: Card, deck: Deck): string | undefined {
  if (card.context) return card.context
  const c = deckContext(card, deck)
  return typeof c === 'string' ? c : c?.label
}

/**
 * The card's header picture, from the pack: its own (a script card's example word), else the
 * picture of the deck context it's met in. None when a card names its own context.
 */
export function artFor(card: Card, deck: Deck): string | undefined {
  if (card.art) return card.art
  if (card.context) return undefined
  const c = deckContext(card, deck)
  return typeof c === 'string' ? undefined : c?.art
}

/** A card's context line and picture, as item fields (both optional). */
export function sceneFor(card: Card, deck: Deck): { context?: string; art?: string } {
  const context = contextFor(card, deck)
  const art = artFor(card, deck)
  return { ...(context ? { context } : {}), ...(art ? { art } : {}) }
}

/** "the piste, the pisted area of a ski field" → "the piste"; "a — as in アイス" → "a". */
export function shortMeaning(meaning: string): string {
  return meaning.split(/[,;—(]| - /)[0]!.trim()
}

export function shuffle<T>(items: readonly T[], rand: Rng): T[] {
  const a = [...items]
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1))
    ;[a[i], a[j]] = [a[j]!, a[i]!]
  }
  return a
}

/**
 * Four options: the answer plus three distinct wrong ones, preferring `near` (look-alikes,
 * same tag) and topping up from `far`. Returns options in random order and the answer index.
 */
export function choices(answer: string, near: string[], far: string[], rand: Rng, n = 4): { options: string[]; answer: number } {
  const wrong: string[] = []
  const take = (list: string[]) => {
    for (const x of shuffle(list, rand)) {
      if (wrong.length >= n - 1) return
      if (x && x !== answer && !wrong.includes(x)) wrong.push(x)
    }
  }
  take(near)
  take(far)
  const options = shuffle([answer, ...wrong], rand)
  return { options, answer: options.indexOf(answer) }
}

export function cardsOf(pack: Pack, deckId: string): Card[] {
  return pack.decks.find((d) => d.id === deckId)?.cards ?? []
}

/** The decks the kit says feed a Learn machine (Deck.learn, B7), in the pack's deck order. */
export function learnDecks(pack: Pack, use: LearnUse): Deck[] {
  return pack.decks.filter((d) => d.learn?.includes(use))
}
